import React, { useState, useEffect } from 'react';
import { formatDate } from '../utils/helpers';

// New Animated Envelope Component
const AnimatedEnvelope = ({ onClick, isOpening }) => {
  return (
    <>
      <style>{`
        .envelope-svg:hover {
          transform: scale(1.05);
        }
        .envelope-svg.opening .letter-svg {
          transform: translateY(-40px);
        }
      `}</style>
      <button onClick={onClick} className="relative group focus:outline-none">
        <svg
          className={`envelope-svg w-48 h-48 transition-transform duration-200 ${isOpening ? 'opening' : ''}`}
          viewBox="0 0 100 100"
        >
          <g>
            {/* Letter (white) - Initially positioned to be mostly hidden */}
            <rect className="letter-svg transition-transform duration-500 ease-out" x="15" y="30" width="70" height="50" fill="white" rx="4" />

            {/* Envelope Body */}
            <path d="M10 40 L10 85 L 90 85 L 90 40 L 50 65 Z" fill="#D4AF37" />

            {/* Envelope Flap */}
            <path d="M10 40 L50 65 L90 40 Z" fill="#c09a2e" />
          </g>
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-md font-bold text-white bg-black/50 rounded-full w-24 h-24 m-auto opacity-0 group-hover:opacity-100 transition-opacity">
          CLICK TO OPEN
        </div>
      </button>
    </>
  );
};


const UnlockingCeremony = ({ capsule, onClose, isAlreadyOpened, onMarkAsOpened, friends = [], onReply }) => {
  const [isRevealed, setIsRevealed] = useState(isAlreadyOpened);
  const [isOpening, setIsOpening] = useState(false);
  const sealedDate = capsule.createdAt || new Date();

  const sender = friends.find(f => f && f.email && capsule && capsule.senderEmail && f.email.toLowerCase() === capsule.senderEmail.toLowerCase());
  const senderName = sender ? `@${sender.username}` : capsule.senderEmail;

  useEffect(() => {
    if (isAlreadyOpened) {
      setIsRevealed(true);
    }
  }, [isAlreadyOpened]);

  const handleReveal = () => {
    if (isOpening) return;

    // Play sound effect - user needs to add this file to /public/sounds/


    setIsOpening(true);

    // Wait for animation to play before showing content
    setTimeout(() => {
      onMarkAsOpened();
      setIsRevealed(true);
    }, 800); // Corresponds to animation delays
  };

  const handleReply = () => {
    if (onReply) {
      // Determine the best identifier to use for the reply
      // If the sender is a friend, use their username (e.g., @john)
      // Otherwise, use their email
      const replyTo = sender ? `@${sender.username}` : capsule.senderEmail;
      const replyTitle = `Re: ${capsule.title}`;
      onReply(replyTo, replyTitle);
      onClose();
    }
  };

  const MediaDisplay = () => {
    if (!capsule.media || capsule.media.length === 0) return null;
    return (
      <div className="mt-6 space-y-4">
        {capsule.media.map((mediaItem, index) => {
          if (mediaItem.type.startsWith('image/')) return <img key={index} src={mediaItem.url} alt={`Capsule media ${index + 1}`} className="rounded-lg max-h-96 mx-auto" />;
          if (mediaItem.type.startsWith('video/')) return <video key={index} src={mediaItem.url} controls className="rounded-lg w-full" />;
          if (mediaItem.type.startsWith('audio/')) {
            console.log("Audio URL:", mediaItem.url);
            return <audio
              key={index}
              src={mediaItem.url}
              controls
              autoPlay
              className="w-full"
              onError={(e) => console.error("Audio Playback Error:", e.target.error)}
            />;
          }
          return null;
        })}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 animate-fade-in" onClick={onClose}>
      <div className="bg-[#1E1E1E] border border-[#D4AF37]/50 rounded-xl shadow-2xl w-full max-w-2xl m-4 text-[#EAEAEA] p-8 relative" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-3xl text-[#A0A0A0] hover:text-[#EAEAEA] z-20">&times;</button>
        {!isRevealed ? (
          <div className="text-center transition-opacity duration-500">
            <h2 className="text-2xl mb-4">A capsule from <span className="font-bold text-[#D4AF37]">{senderName}</span> is now ready.</h2>
            <p className="text-[#A0A0A0] mb-8">It was sealed on {formatDate(sealedDate)}</p>
            <AnimatedEnvelope onClick={handleReveal} isOpening={isOpening} />
            <p className="mt-4 text-[#D4AF37] animate-pulse">Click the seal to reveal the message</p>
          </div>
        ) : (
          <div className="animate-fade-in max-h-[80vh] overflow-y-auto pr-4">
            <h2 className="text-3xl font-bold text-[#D4AF37] mb-4">{capsule.title}</h2>
            <MediaDisplay />
            <p className="text-lg leading-relaxed whitespace-pre-wrap text-[#EAEAEA] mt-6">{capsule.message}</p>

            {onReply && (
              <div className="mt-8 flex justify-center">
                <button
                  onClick={handleReply}
                  className="bg-[#D4AF37] text-[#121212] font-bold py-2 px-6 rounded-full hover:bg-[#C09A2E] transition-colors flex items-center space-x-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M7.707 3.293a1 1 0 010 1.414L5.414 7H11a7 7 0 017 7v2a1 1 0 11-2 0v-2a5 5 0 00-5-5H5.414l2.293 2.293a1 1 0 11-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Reply</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UnlockingCeremony;
